// extraCardRender.js – Extrakarten (Spielideen, Legende …) als Druckbild
// Gleicher Kartenaufbau wie die Memo-Karten: runder Rahmen, weisse Fläche, Handschrift.
import { EXTRA_PURPLE } from './extraCards';

const CARD_PX = Math.round((6 / 2.54) * 300); // 709 = 6 cm bei 300 dpi
const FONT = "'MemoHand', 'Patrick Hand SC', 'Comic Sans MS', cursive";
const SKEW = Math.tan((-9 * Math.PI) / 180);
const LINE_PURPLE = '#b39bd0';
const INK = '#111';

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

// „Text mit [violetten] Stellen“ → Wörter mit Farbe
function words(text, baseColor) {
  const out = [];
  let purple = false;
  text.split(/(\[|\])/).forEach(part => {
    if (part === '[') { purple = true; return; }
    if (part === ']') { purple = false; return; }
    part.split(/(\s+)/).forEach(w => {
      if (!w) return;
      if (/^\s+$/.test(w)) out.push({ space: true });
      else out.push({ text: w, color: purple ? EXTRA_PURPLE : baseColor });
    });
  });
  return out;
}

// Wörter in Zeilen umbrechen; jede Zeile = Liste von Segmenten {text, color}
function wrap(ctx, ws, maxW) {
  const lines = [];
  let line = [], width = 0, pendingSpace = false;
  const spaceW = ctx.measureText(' ').width;
  for (const w of ws) {
    if (w.space) { pendingSpace = line.length > 0; continue; }
    const ww = ctx.measureText(w.text).width;
    const add = (pendingSpace ? spaceW : 0) + ww;
    if (line.length && width + add > maxW) {
      lines.push({ segs: line, width });
      line = [{ ...w }]; width = ww;
    } else {
      if (pendingSpace && line.length) line.push({ text: ' ', color: w.color });
      line.push({ ...w }); width += add;
    }
    pendingSpace = false;
  }
  if (line.length) lines.push({ segs: line, width });
  return lines;
}

function drawSegs(ctx, segs, x, baseline, fs, strokeFactor) {
  ctx.save();
  ctx.translate(x, baseline);
  ctx.transform(1, 0, SKEW, 1, 0, 0);
  ctx.font = `${fs}px ${FONT}`;
  ctx.textBaseline = 'alphabetic';
  ctx.textAlign = 'left';
  ctx.lineJoin = 'round';
  ctx.lineWidth = strokeFactor * fs;
  let cx = 0;
  for (const s of segs) {
    ctx.fillStyle = s.color;
    ctx.strokeStyle = s.color;
    ctx.fillText(s.text, cx, 0);
    if (strokeFactor) ctx.strokeText(s.text, cx, 0);
    cx += ctx.measureText(s.text).width;
  }
  ctx.restore();
}

// Inhalt eines Kartenkörpers in Zeilen auslegen (bei Schriftgrösse fs)
function layoutBody(ctx, card, fs, width) {
  ctx.font = `${fs}px ${FONT}`;
  const pitch = fs * 1.36;
  const indent = ctx.measureText('0. ').width * 1.15;
  const rows = [];   // {segs, x, dy, prefix?}
  let y = 0;
  for (const item of card.body) {
    if (item.gap) { y += pitch * 0.7; continue; }
    if (item.zb) { rows.push({ segs: [{ text: 'z. B.:', color: EXTRA_PURPLE }], width: ctx.measureText('z. B.:').width, x: 0, y }); y += pitch; continue; }
    let prefix = null, text, color = INK, ind = 0;
    if (item.n !== undefined) { prefix = { text: `${item.n}.`, color: INK }; text = item.text; ind = indent; }
    else if (item.b !== undefined) { prefix = { text: '•', color: EXTRA_PURPLE }; text = item.b; color = EXTRA_PURPLE; ind = indent * 0.75; }
    else if (item.v !== undefined) { text = item.v; color = EXTRA_PURPLE; }
    else text = item.p;
    const lines = wrap(ctx, words(text, color), width - ind);
    for (let i = 0; i < lines.length; i++) {
      rows.push({ segs: lines[i].segs, width: lines[i].width, x: ind, y, prefix: i === 0 ? prefix : null });
      y += pitch;
    }
  }
  return { rows, height: y - pitch + fs * 0.75 };
}

export async function renderExtraCardCanvas(card, W = CARD_PX) {
  const sample = [card.titel, ...(card.untertitel || []), ...card.body.map(b => b.text || b.b || b.p || b.v || '')].join(' ');
  await document.fonts.load(`${Math.round(W * 0.1)}px MemoHand`, sample).catch(() => {});
  const canvas = document.createElement('canvas');
  canvas.width = W; canvas.height = W;
  const ctx = canvas.getContext('2d');

  // violetter Rahmen mit runden Ecken, weisse Fläche
  roundRect(ctx, 0, 0, W, W, 0.083 * W);
  ctx.fillStyle = EXTRA_PURPLE;
  ctx.fill();
  const inset = 0.094 * W;
  const I = W - 2 * inset;
  ctx.fillStyle = '#fff';
  ctx.fillRect(inset, inset, I, I);

  const padX = 0.045 * I;
  const textW = I - 2 * padX;
  const cx = inset + I / 2;
  let y = inset + 0.035 * I;

  // Titel (Zeilenumbruch über \n), so gross wie möglich
  const titleLines = card.titel.split('\n');
  ctx.font = `100px ${FONT}`;
  const widest = Math.max(...titleLines.map(t => ctx.measureText(t).width));
  const tfs = Math.min(0.13 * I, (textW * 100) / widest);
  ctx.font = `${tfs}px ${FONT}`;
  for (const t of titleLines) {
    y += tfs * 0.68;
    const w = ctx.measureText(t).width;
    drawSegs(ctx, [{ text: t, color: INK }], cx - w / 2, y, tfs, 0.035);
    y += tfs * 0.14;
  }

  // Untertitel (violett)
  const subs = card.untertitel || [];
  if (subs.length) {
    ctx.font = `100px ${FONT}`;
    const sw = Math.max(...subs.map(t => ctx.measureText(t).width));
    const sfs = Math.min(0.056 * I, (textW * 100) / sw);
    ctx.font = `${sfs}px ${FONT}`;
    y += sfs * (card.center ? 1.0 : 0.35);
    for (const t of subs) {
      y += sfs * 0.75;
      const w = ctx.measureText(t).width;
      drawSegs(ctx, [{ text: t, color: EXTRA_PURPLE }], cx - w / 2, y, sfs, 0.012);
      y += sfs * 0.38;
    }
  }

  // Trennlinie
  y += 0.025 * I;
  ctx.fillStyle = LINE_PURPLE;
  ctx.fillRect(inset + 0.02 * I, y, I - 0.04 * I, 0.009 * I);
  y += 0.009 * I;

  // Körper: Schrift so verkleinern, bis alles Platz hat; vertikal zentriert
  const top = y + 0.035 * I;
  const bottom = inset + I - 0.035 * I;
  const avail = bottom - top;
  let fs = 0.07 * I, lay;
  for (let k = 0; k < 40; k++) {
    lay = layoutBody(ctx, card, fs, textW);
    if (lay.height <= avail) break;
    fs *= 0.96;
  }
  const start = top + Math.max(0, (avail - lay.height) / 2) + fs * 0.66;
  const left = inset + padX;
  for (const r of lay.rows) {
    const bx = card.center ? cx - r.width / 2 : left + r.x;
    if (r.prefix && r.prefix.text === '•') {
      ctx.beginPath();
      ctx.arc(left + fs * 0.18, start + r.y - fs * 0.24, fs * 0.11, 0, Math.PI * 2);
      ctx.fillStyle = r.prefix.color;
      ctx.fill();
    } else if (r.prefix) drawSegs(ctx, [r.prefix], left, start + r.y, fs, 0.012);
    drawSegs(ctx, r.segs, bx, start + r.y, fs, 0.012);
  }
  return canvas;
}
